import { useState } from "react";
import { useI18n } from "../i18n/LanguageContext";

/**
 * BankDetailsCard — shown on the client dashboard. If the admin has not
 * yet populated bank coordinates, an empty-state message is rendered.
 * Each populated value gets a copy-to-clipboard button (clipboard API
 * is best-effort; falls back silently on failure).
 */
export default function BankDetailsCard({ bankDetails }) {
  const { t } = useI18n();
  const [copiedKey, setCopiedKey] = useState(null);
  const bd = bankDetails || {};
  const hasAny = Boolean(
    bd.beneficiary || bd.iban || bd.bic || bd.bank || bd.reference_template,
  );

  const copy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1400);
    } catch (err) {
      // Clipboard API unavailable (permissions, http, sandbox) — degrade gracefully.
      console.debug("[bank-details] clipboard copy skipped:", err?.message || err);
    }
  };

  const rows = [
    { key: "beneficiary", label: t("bank.beneficiary"), value: bd.beneficiary, mono: false },
    { key: "iban", label: t("bank.iban"), value: bd.iban, mono: true },
    { key: "bic", label: t("bank.bic"), value: bd.bic, mono: true },
    { key: "bank", label: t("bank.bank"), value: bd.bank, mono: false },
    {
      key: "reference",
      label: t("bank.reference"),
      value: bd.reference_template,
      mono: true,
    },
  ];

  return (
    <div
      className="mt-14 br-card overflow-hidden br-fade-up br-delay-3"
      data-testid="bank-details-card"
    >
      <div
        className="px-8 py-7 border-b"
        style={{
          borderColor: "var(--br-line)",
          background: "linear-gradient(180deg, rgba(167,128,73,0.05), transparent)",
        }}
      >
        <div className="br-overline">{t("bank.overline")}</div>
        <h2 className="font-serif-display text-[1.8rem] mt-3 text-[color:var(--br-parchment)]">
          {t("bank.title")}
        </h2>
        <p className="text-[0.92rem] text-[color:var(--br-text-dim)] mt-3 leading-relaxed max-w-[640px]">
          {t("bank.intro")}
        </p>
      </div>

      {hasAny ? (
        <>
          <div className="divide-y" style={{ borderColor: "var(--br-line)" }}>
            {rows.map((r) => (
              <BankRow
                key={r.key}
                label={r.label}
                value={r.value}
                mono={r.mono}
                copied={copiedKey === r.key}
                onCopy={() => copy(r.key, r.value)}
              />
            ))}
          </div>
          <div
            className="px-8 py-5 text-[0.78rem] text-[color:var(--br-text-dim)] leading-relaxed"
            style={{
              borderTop: "1px solid var(--br-line)",
              background: "rgba(179,107,107,0.04)",
            }}
            data-testid="bank-disclaimer"
          >
            {t("bank.disclaimer")}
          </div>
        </>
      ) : (
        <div
          className="px-8 py-12 text-center"
          data-testid="bank-details-empty"
        >
          <div
            className="font-mono-fin text-[0.66rem] tracking-[0.28em] uppercase"
            style={{ color: "var(--br-bronze)" }}
          >
            {t("bank.empty_title")}
          </div>
          <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-4 leading-relaxed max-w-[520px] mx-auto">
            {t("bank.empty_body")}
          </p>
        </div>
      )}
    </div>
  );
}

function BankRow({ label, value, mono, copied, onCopy }) {
  const { t } = useI18n();
  if (!value) return null;
  return (
    <div
      className="px-5 sm:px-8 py-5"
      style={{ borderBottom: "1px solid var(--br-line)" }}
      data-testid={`bank-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase text-[color:var(--br-text-dim)]">
          {label}
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="br-btn-outline flex-shrink-0"
          style={{ padding: "6px 12px", fontSize: "0.62rem" }}
          data-testid={`bank-copy-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {copied ? t("bank.copied") : t("bank.copy")}
        </button>
      </div>
      <div
        className={`mt-2 ${
          mono ? "font-mono-fin text-[0.95rem] sm:text-[1.05rem]" : "text-[0.95rem] sm:text-[1rem]"
        } text-[color:var(--br-parchment)] leading-relaxed`}
        style={{ overflowWrap: "anywhere" }}
      >
        {value}
      </div>
    </div>
  );
}
